import { LinearProgress } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { io } from 'socket.io-client';
import api from "../../BrandOnBoarding/api";
import AIGenerated from "../../Component/AIGenerated/AIGeneratedV2";
import Header from "../../Component/Header/Header";
import PDFFooter from "../../Component/PDFFooter/PDFFooter";
import PDFHeader from "../../Component/PDFHeader/PDFHeader";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import Tabs from "../../Component/UI/Tabs/Tabs";
import { AuthContext } from "../../context/auth-context";
import { useDailyLimits } from "../../hooks/useDailyLimits";
import ProductSpecs from "../CategoryDiscovery/ProductSpecs/ProductSpecs";
import RealtimeActions from "./Actions/Actions";
import BrandScore from "./BrandScore/BrandScore";
import CategoryDimensions from "./CategoryDimensions/CategoryDimensions";
import Competitors from "./Competitors/Competitors";
import CoreAttributes from "./CoreAttributes/CoreAttributes";
import { DiscoveryContext } from "./discovery-context";
import classes from "./Discovery.module.css";
import Form from "./Form";
import History from "./History/History";
import useHistory from "./History/useHistory";
import Sources from "./Sources/Sources";
import Taxonomy from "./Taxonomy/Taxonomy";

export default function DiscoveryV2() {
  const [inputValues, setInputValues] = useState({
    brand: "",
    category: "",
    market: "",
    llm: "",
    type: "brand",
  });
  const [tabSelected, setTabSelected] = useState("overview");
  const [externalReportLoading, setExternalReportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [childKey, setChildKey] = useState(0);
  const discoveryCtx = useContext(DiscoveryContext);
  const socketRef = useRef(null);
  const transactionIdRef = useRef(null);
  const discoveryCtxRef = useRef(discoveryCtx);
  const pollingRef = useRef(null);
  const [cancelTransaction, setCancelTransaction] = useState(false);
  const [isHistoryRefresh, setIsHistoryRefresh] = useState(false);
  const { getHistory } = useHistory(discoveryCtx.type);
  const remountChild = () => {
    setChildKey((prev) => prev + 1);
  };

  const {
    launchText,
    updateLaunches,
    launchesRemaining,
    isSpecial,
    frequency: launchLimitFrequency,
  } = useDailyLimits("Discovery");

  const abortControllerRef = useRef(null);
  const location = useLocation();

  const entity = discoveryCtx.entity;
  const discoveryType = discoveryCtx.type || "brand";
  const discoveryTotalCount = 22;
  const pdfRef = useRef();
  const rankScoreAbortCtrRef = useRef(null);
  const { user } = useContext(AuthContext);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
  let brandData = discoveryCtx.brand_scores;
  let brandDataCompetitors = brandData.slice(1);
  const highestFavorability = Math.max(...brandDataCompetitors.map((item) => item?.favorability ?? 0));
  const highestSentiment = brandDataCompetitors.find(
    (item) => item?.favorability === highestFavorability
  );

  function tabChangeHandler(val) {
    setTabSelected(val);
  }
  let currentBrandSnippets = [];
  let currentBrandThemes = [];
  const coreAttributeMapRef = useRef({});
  const loadSocketData = (socketResponse, requestObj, currentTransactionId) => {
    if (!socketResponse || typeof socketResponse !== 'object') {
      console.error("Invalid socket response:", socketResponse);
      return;
    }

    let { type, request, response, category, llm, status, transactionId } = socketResponse;

    if (!type || !request) {
      console.error("Incomplete socket response:", response);
      return;
    }
    if (status !== 'COMPLETED') {
      return false
    }
    if (parseInt(transactionId) !== parseInt(currentTransactionId)) return;
    console.log("LoadedDiscoveryResponse", response)
    switch (type) {
      case "rank":
        if (!response) return console.warn("Missing response for rank");
        const sources = [...Object.keys(response?.sources?.rank || {})];
        response = { ...response, request_id: socketResponse.id };
        discoveryCtx.updateSources(sources);
        discoveryCtx.updateRankScores(response);
        break;

      case "sentiment":
        if (!response) return console.warn("Missing response/Transaction for sentiment");

        const entity = { ...response?.data }?.[0];
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
            name: entity?.name || "Unknown",
            favorability: entity?.score ?? 0,
            extras: response.extras,
          };

        let index = -1;
        try {
          requestObj.competitor?.[request.category]?.forEach((item, idx) => {
            if (item.name?.toLowerCase() == entity?.name?.toLowerCase()) {
              index = idx;
            }
          }
          );
          if (requestObj.brand === entity?.name) index = -1;
        } catch (err) {
          console.warn("Error calculating sentiment index:", err);
        }
        const sentimentSources = [
          ...Object.keys(response?.sources?.sentiment || {}),
        ];
        discoveryCtx.updateBrandScores({
          data: brandRes,
          index: index + 1
        });
        discoveryCtx.updateSources(sentimentSources);
        break;

      case "signalStrength":
        if (!response) return console.warn("Missing response for signalStrength");

        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];
        const sourcesPosNeg = [
          ...(response?.aggregatedPositiveSources || []),
          ...(response?.aggregatedNegativeSources || []),
        ];

        discoveryCtx.updatePositiveNegativeAttributes({
          name: socketResponse.brand,
          positives: positiveAttributes.slice(0, 10),
          negatives: negativeAttributes.slice(0, 10),
        });

        discoveryCtx.updateSources(sourcesPosNeg);
        break;

      case "categoryDimension":
        if (!response) return console.warn("Missing response for categoryDimension");

        discoveryCtx.updateCategoryDimensions({
          data: response?.data || [],
          summary: response?.summary || "",
        });

        const sourcesCategoryDim = [
          ...Object.keys(response?.sources?.category || {}),
          ...Object.keys(response?.sources?.dimension || {}),
        ];

        discoveryCtx.updateSources(sourcesCategoryDim);
        break;

      case "taxonomy":
        if (!response) return console.warn("Missing response for taxonomy");
        discoveryCtx.updatedTaxonomies(response.data);
        break;

      case "core-attribute":
        if (!response || !category || !llm) return console.warn("Missing data for core-attribute");

        const key = `${llm}__${category}`;

        if (!coreAttributeMapRef.current[key]) {
          coreAttributeMapRef.current[key] = [];
        }

        const newProductName = response?.products?.[0]?.name?.toLowerCase().trim();
        const alreadyExists = coreAttributeMapRef.current[key].some(
          (item) =>
            item?.products?.[0]?.name?.toLowerCase().trim() === newProductName
        );
        if (!alreadyExists && newProductName) {
          coreAttributeMapRef.current[key].push(response);
        }
        console.log("coreAttributeMapLoad", coreAttributeMapRef.current, coreAttributeMapRef.current[key], discoveryCtx)
        console.log("discoverycoreattribute", discoveryCtx)
        if (coreAttributeMapRef.current[key].length === 4) {
          const group = coreAttributeMapRef.current[key];
          let coreAttributeSummary = "";
          const products = [];
          const sources = [];

          const category_averages = group[0]?.category_averages;
          console.log("Discoverycoreattribute", requestObj?.competitor, category, requestObj?.competitor?.[category])
          let competitors = requestObj?.competitor?.[category].map((item) => item.name);
          let brand = requestObj?.brand;
          if (competitors) {
            competitors.unshift(brand);
          } else {
            competitors = [];
          }

          for (const res of group) {
            if (!res?.error && Array.isArray(res?.products)) {
              coreAttributeSummary += res.products?.[0]?.summary || "";
              coreAttributeSummary += "\n\n";
              products.push(res.products?.[0]);
              sources.push(
                ...Object.keys(res?.sources?.category || {}),
                ...Object.keys(res?.sources?.product || {})
              );
            }
          }

          let sortedProducts = [];
          for (const company of competitors) {
            const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
            for (const product of products) {
              const normalizedProductName = product.name.toLowerCase().replace(/\s+/g, "");
              if (normalizedProductName === normalizedCompany) {
                sortedProducts.push(product);
                break;
              }
            }
          }

          discoveryCtx.updateCoreAttributes({
            category_averages,
            products: sortedProducts,
            summary: coreAttributeSummary,
          });

          discoveryCtx.updateSources(sources);

          delete coreAttributeMapRef.current[key];
        }
        break;

      case "description":
        if (!response?.data) return console.warn("Missing response.data for description");
        discoveryCtx.updateBrandDescription(response.data);
        break;

      case "theme":
      case "snippet": {
        const isTheme = type === "theme";
        const dataSnippet = response?.data?.data;

        if (!Array.isArray(dataSnippet)) {
          console.warn(`${type} response is not a valid array:`, dataSnippet);
          return;
        }

        // const llm = transaction?.primaryLLM;
        console.log("llmdata", llm, category)
        if (!llm || !category) {
          console.warn("Missing llm/category for", type);
          return;
        }

        const targetStore = isTheme ? currentBrandSnippets : currentBrandThemes;
        const contextMethod = isTheme
          ? discoveryCtx.updateBrandSnippets
          : discoveryCtx.updatedBrandThemes;
        if (!targetStore) targetStore = {};
        if (!targetStore) targetStore = [];
        targetStore.push(dataSnippet)
        contextMethod({
          res: targetStore
        });

        break;
      }

      default:
        console.warn(`Unhandled socket data type: ${type}`);
    }
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    const type = searchParams.get("type"); // this will be "brand" or "product"
    const customer_id = searchParams.get("customer_id");
    if (id && type && customer_id && !cancelTransaction) {
      loadDiscovery(id, type, customer_id, false);
    }
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (rankScoreAbortCtrRef.current) {
        rankScoreAbortCtrRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  async function loadDiscovery(id, type, customer_id, refreshRecord) {
    try {

      api.get(
        `${webSocketBaseURL}/discovery/${customer_id}/${id}`
      ).then((response) => {
        if (response?.data?.transactionStatus !== "PENDING") {
          const { totalCount, completedTaskCount, transactionStatus, task = [] } = response?.data;
          if (refreshRecord) {
            currentBrandSnippets = [];
            currentBrandThemes = [];
            coreAttributeMapRef.current = {};
            discoveryCtx.refreshState()
          } else {
            discoveryCtx.resetState();
          }
          discoveryCtx.updateTransactionId(id)
          discoveryCtx.updateId(id);
          discoveryCtx.updatedLaunched(true);
          if (transactionStatus === "COMPLETED" || transactionStatus === "RETRY" || completedTaskCount === totalCount) {
            setIsHistoryRefresh(prev => !prev);
            stopPolling();
            discoveryCtx.updateLoading(false);
          }
          let requestObj = task[0]?.request_object
          let competitors = requestObj.competitor[requestObj.category];
          competitors = [{ name: requestObj.brand, domain: requestObj.brandDomainName, variants: [], display_name: requestObj.brand }, ...competitors];
          discoveryCtx.updatedCompetitors(competitors);
          discoveryCtx.updateTransactionStatus(transactionStatus);
          discoveryCtx.updateEntity(requestObj.brand);
          discoveryCtx.updateMarket(requestObj.market.join(','));
          discoveryCtx.updateCategory(requestObj.category[0]);
          discoveryCtx.updateCategoryType(requestObj?.categoryType);
          discoveryCtx.updateLLM(requestObj.llm[0]);
          discoveryCtx.updateType(type);
          discoveryCtx.updateName(`${requestObj.brand} Brand Overview`);
          discoveryCtx.updateStatus({ totalCount, completedTaskCount });
          for (const record of task) {
            loadSocketData(record, requestObj, id)
          }
        }
      })
        .catch((error) => {
          console.error("API call failed:", error);
          if (error.message === "Network Error") {
            toast.warn("Please check your network connection");
          } else if (error.code === "ECONNABORTED") {
            toast.warn("Request timed out. Please try again");
          } else {
            toast.error("Failed to load discovery report. Please check if URL is correct");
          }
        })
    } catch (error) {
      toast.error(
        "Failed to load discovery report. Please check if URL is correct"
      );
    } finally {
      setExternalReportLoading(false);
    }
  }

  let coreAttributesSummary = "";
  let categoryDimensionsSummary = "";
  let productDimensionSummary = "";
  if (
    discoveryCtx?.core_attributes &&
    Object.keys(discoveryCtx?.core_attributes).length > 0
  ) {
    coreAttributesSummary = discoveryCtx.core_attributes.summary;
  }
  if (
    discoveryCtx?.category_dimensions &&
    Object.keys(discoveryCtx?.category_dimensions).length > 0
  ) {
    categoryDimensionsSummary = discoveryCtx.category_dimensions.summary;
  }

  if (
    discoveryCtx?.taxonomies &&
    Object.keys(discoveryCtx?.taxonomies).length > 0
  ) {
    productDimensionSummary = discoveryCtx.taxonomies.summary;
  }

  let competitors = discoveryCtx.competitors;

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${entity} Overview`,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: 380mm 400mm;
        margin-left: 10mm;
        margin-right: 10mm;
        margin-bottom: 5mm;
        margin-top: 15mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact; /* Ensures accurate color printing */
        }
        .page-footer {
          position: fixed;
          bottom: 10mm;
          left: 10mm;
          right: 10mm;
          font-size: 10pt;
          color: #777;
          text-align: center;
        }
      }
    `,
  });

  async function generateReport() {
    try {
      setLoading(true);
      const frontend_build_version = localStorage.getItem(
        "frontend_build_version"
      );
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      const payload = {
        isBrand: discoveryType === "brand",
        request_type: "discoveryV2",
        transactionID: discoveryCtx.state.transactionId,
        CompetitorName: highestSentiment.name,
        brand: entity,
        category: discoveryCtx.category,
        market: discoveryCtx.market,
        primaryLLM: discoveryCtx.llm,
        frontend_build_version,
        token,
        user,
      };
      console.log(payload);

      const blob = await api.post(
        "/llm-reporting/generate/discovery",
        payload,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(blob.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Discovery.pptx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!socketRef.current) {
      const isProd = process.env.REACT_APP_ENV === "production";

      const socketOptions = {
        reconnectionAttempts: 5,
      };

      const socketUrl = process.env.REACT_APP_SOCKET_BASE_URL;

      if (isProd) {
        socketOptions.path = "/ows/";
      } else {
        socketOptions.transports = ["websocket"];
      }

      // Initialize socket
      socketRef.current = io(socketUrl, socketOptions);

      socketRef.current.on("connect", () => {
        console.log("Socket connected:", socketRef.current.id);
      });

      socketRef.current.on("disconnect", () => {
        console.warn("Socket disconnected");
      });

      socketRef.current.on("live-data", handleSocketData);
    }

    const handleOnline = () => {
      if (socketRef.current) {
        if (!socketRef.current.connected && !socketRef.current.active) {
          console.log("Network back online, reconnecting socket...");
          socketRef.current.connect();
        } else {
          console.log("Socket already connected or reconnecting, skipping...");
        }
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      // Optional: Cleanup if needed
      // socketRef.current?.disconnect();
      window.removeEventListener("online", handleOnline);
      transactionIdRef.current = null
    };
  }, []);

  useEffect(() => {
    if (discoveryCtx.state.transactionId) {
      transactionIdRef.current = discoveryCtx.state.transactionId;
    }
    else {
      transactionIdRef.current = null
    }
  }, [discoveryCtx.state.transactionId]);

  useEffect(() => {
    discoveryCtxRef.current = discoveryCtx;
  }, [discoveryCtx]);
  console.log("discoveryCtx", discoveryCtx)

  const handleSocketData = (data) => {
    if (!data) {
      console.error("Received null or undefined socket data.");
      return;
    }
    let transactionId, type, request, response, category, llm, transaction, completedTaskCount;

    if (Array.isArray(data?.coreAttributeData)) {
      if (!data?.coreAttributeData[0]) {
        console.error("Array socket data is empty or invalid:", data?.coreAttributeData);
        return;
      }
      transactionId = data?.coreAttributeData[0]?.transaction?.transaction_id;
      type = data?.coreAttributeData[0]?.type;
      completedTaskCount = data?.completedTaskCount
    } else {
      ({ type, request, response, category, llm, transaction, completedTaskCount } = data);
      transactionId = transaction?.transaction_id ? transaction?.transaction_id : data?.transactionId;
    }

    const currentContextTxnId = transactionIdRef.current;
    if (transactionId !== currentContextTxnId) return;
    if (data?.status === "Transaction Successfull" || data?.status === "Transaction failed") {
      setIsHistoryRefresh(prev => !prev);

      stopPolling();
      discoveryCtx.updateTransactionStatus(data?.status === "Transaction Successfull" ? "COMPLETED" : "RETRY")
      discoveryCtx.updateLoading(false);
    }
    discoveryCtx.updateStatus({ completedTaskCount })

    switch (type) {
      case "rank":
        if (!response) return console.warn("Missing response for 'rank'");
        response = { ...response, request_id: data.id };
        const sources = [...Object.keys(response?.sources?.rank || {})];
        discoveryCtx.updateSources(sources);
        discoveryCtx.updateRankScores(response);
        break;

      case "sentiment":
        if (!response || !transaction || !request?.category) {
          console.warn("Invalid sentiment data:", response, transaction);
          return;
        }

        const entity = response?.data?.[0];
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
            name: entity?.name || "Unknown",
            favorability: entity?.score ?? 0,
            extras: response.extras,
          };
        const sentimentSources = [
          ...Object.keys(response?.sources?.sentiment || {}),
        ];
        const originalCompetitors = discoveryCtxRef.current?.competitors || [];
        const competitors = originalCompetitors.slice(1);
        let index = competitors.findIndex((item) => item.name?.toLowerCase() == entity?.name?.toLowerCase());
        console.log("sentimentcompetitors", discoveryCtxRef, competitors, index)
        if (transaction?.brand === entity?.name) index = -1;

        discoveryCtx.updateBrandScores({
          data: brandRes,
          index: index + 1
        });
        discoveryCtx.updateSources(sentimentSources);
        break;

      case "signalStrength":
        if (!response) return console.warn("Missing response for 'signalStrength'");
        console.log("signalStrengthResponse", response)
        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];
        const sourcesPosNeg = [
          ...(response?.aggregatedPositiveSources || []),
          ...(response?.aggregatedNegativeSources || []),
        ];

        discoveryCtx.updatePositiveNegativeAttributes({
          name: data.brand,
          positives: positiveAttributes.slice(0, 10),
          negatives: negativeAttributes.slice(0, 10),
        });

        discoveryCtx.updateSources(sourcesPosNeg);
        break;

      case "categoryDimension":
        if (!response) return console.warn("Missing response for 'categoryDimension'");
        discoveryCtx.updateCategoryDimensions({
          data: response?.data || [],
          summary: response?.summary || "",
        });

        const sourcesCategoryDim = [
          ...Object.keys(response?.sources?.category || {}),
          ...Object.keys(response?.sources?.dimension || {}),
        ];
        discoveryCtx.updateSources(sourcesCategoryDim);
        break;

      case "taxonomy":
        if (!response) return console.warn("Missing response for taxonomy");
        discoveryCtx.updatedTaxonomies(response.data);
        break;

      case "core-attribute":
        if (!Array.isArray(data?.coreAttributeData)) {
          console.warn("Expected array for core-attribute:", data?.coreAttributeData);
          return;
        }
        console.log("CoreattributeDataNew", data)
        // Grouping by category + llm
        if (data?.coreAttributeData.length == 4) {
          const groupedByCategoryAndLlm = data?.coreAttributeData.reduce((acc, item) => {
            const category = item?.category;
            const llm = item?.llm;
            if (!category || !llm) return acc;

            const key = `${category}__${llm}`; // unique key per group
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);

            return acc;
          }, {});

          // Debug log
          console.log("CoreAttributeData", data?.coreAttributeData)
          console.log("CoreAttributeDataGrouped:", groupedByCategoryAndLlm);

          // Process each group
          Object.entries(groupedByCategoryAndLlm).forEach(([key, items]) => {

            const resData = items
              .filter((res) => res?.response)
              .map((res) => res.response);

            let coreAttributeSummary = "";
            let category_averages = resData[0]?.category_averages || {};
            let products = [];
            const sources = [];

            for (let res of resData) {
              if (!res?.error) {
                coreAttributeSummary += res.products?.[0]?.summary || "";
                coreAttributeSummary += "\n\n";
                products.push(res.products?.[0]);
                sources.push(
                  ...Object.keys(res.sources?.category || {}),
                  ...Object.keys(res.sources?.product || {})
                );
              }
            }

            const originalCompetitors =
              discoveryCtxRef.current?.competitors.map((item) => item.name) || [];
            const sortedProducts = [];
            originalCompetitors.forEach((company) => {
              const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
              for (const product of products) {
                const normalizedProductName = product.name.toLowerCase().replace(/\s+/g, "");
                if (normalizedProductName === normalizedCompany) {
                  sortedProducts.push(product);
                  break;
                }
              }
            });


            discoveryCtx.updateCoreAttributes({
              category_averages,
              products: sortedProducts,
              summary: coreAttributeSummary,
            });

            discoveryCtx.updateSources(sources);
          });
        }
        break;


      case "description":
        if (!response?.data) return console.warn("Missing response.data for description");
        discoveryCtx.updateBrandDescription(response.data);
        break;

      case "theme":
      case "snippet": {
        const isTheme = type === "theme";
        const snippetData = response?.data?.data;

        if (!Array.isArray(snippetData)) {
          console.warn(`${type} data not in expected array format:`, snippetData);
          return;
        }

        if (!llm || !category) {
          console.warn("Missing llm or category for theme/snippet");
          return;
        }
        const store = isTheme
          ? discoveryCtxRef.current?.brand_snippets
          : discoveryCtxRef.current?.brand_themes;

        const updateFn = isTheme
          ? discoveryCtx.updateBrandSnippets
          : discoveryCtx.updatedBrandThemes;

        const currentList = store || [];
        const updatedList = [...currentList, snippetData]
        console.log("Themesnippetdata", updatedList, isTheme)
        updateFn({
          res: updatedList,
        });
        break;
      }

      default:
        console.warn(`Unhandled socket data type: ${type}`);
    }
  };

  //start polling after discovery launch
  const startPolling = (transactionId, type) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      console.log("Calling transaction API...");
      loadDiscovery(transactionId, type, user?.user_role?.user_id, true);
    }, 1 * 60 * 1000); // 30 sec
  };
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      console.log("Stopped polling");
    }
  };
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  }, [])

  function ProgressIndicator({ total, completed }) {
    const progressPercentage = (completed / total) * 100;

    return (
      <div className={classes.progressIndicator}>
        <div className={classes.progressBar}>
          <div
            className={classes.progressFill}
            style={{
              width: `${progressPercentage}%`,
            }}
          ></div>
        </div>
        <div className={classes.progressText}>
          {completed} of {total} tasks completed
        </div>
      </div>
    );
  }


  const discoveryCall = async (values) => {
    let category_taxonomies;
    if (discoveryCtx.type == "product") {
      category_taxonomies = discoveryCtx.category_taxonomies ?? discoveryCtx.category_taxonomies;
    }
    const originalCompetitors = discoveryCtx.competitors || [];
    const brandDomain = discoveryCtx.competitors[0]?.domain
    const competitors = originalCompetitors.slice(1);
    const payload = {
      user_id: user?.user_role?.user_id,
      brand: values.brand,
      market: values?.market ? [values?.market] : ["General Market"],
      type: discoveryCtx.type,
      llm: [values?.llm],
      category: [values?.category],
      language: discoveryCtx.language == "it" ? "Italian" : "English",
      competitor: {
        [values.category]: competitors,
      },
      brandDomainName: brandDomain,
      request_type: "discovery",
      shouldProcessImmediately: "true",
      isMock: values.isMock,
      customTaxonomy: category_taxonomies
    }
    try {

      const response = await api.post(
        `${webSocketBaseURL}/brand-discovery`,
        payload
      );
      setIsHistoryRefresh(prev => !prev);
      const transactionId = response?.data?.transaction_id;
      const totalCount = response?.data?.totalCount;
      if (transactionId) {
        startPolling(transactionId, discoveryCtx.type);
        discoveryCtx.updateStatus({ totalCount, completedTaskCount: 0 });
      }
      discoveryCtx.updateTransactionId(transactionId);
      discoveryCtx.updateId(transactionId);
      if (!transactionId) {
        console.error("Transaction ID not returned");
        return;
      }
    } catch (error) {
      console.error("Error Occur in Discovery", error);
    }
  };

  return (
    <>
      {/* {externalReportLoading && <LinearProgress />} */}
      <section
        className={classes.content_section}
        style={{
          gridTemplateRows: discoveryType === "category" && "auto 1fr",
        }}
      >
        <Header actionText={launchText} actionVariant="text" />
        <div
          className={classes.container}
          style={{
            gridTemplateColumns: discoveryType === "category" && "1fr",
          }}
        >
          <div className={classes.content}>
            <Tabs
              options={[]}
              onChange={tabChangeHandler}
              selected={tabSelected}
            />
            <Form
              warningText={warningText}
              setWarningText={setWarningText}
              cancelTransaction={cancelTransaction}
              setCancelTransaction={setCancelTransaction}
              stopPolling={stopPolling}
              id="discovery"
              onReset={() => {
                setInputValues((prevValues) => {
                  return {
                    ...prevValues,
                    brand: "",
                    category: "",
                    market: "",
                    llm: "",
                  };
                });
                setWarningText("");
              }}
              onSubmit={(values) => {
                if (launchesRemaining == 0 && !isSpecial) {
                  toast.warn(
                    `You have reached your ${launchLimitFrequency} limit`
                  );
                  return;
                }
                if (!isSpecial) updateLaunches();
                if (externalReportLoading) return;
                if (values.type !== "category") {
                  discoveryCtx.updateEntity(values.brand);
                }
                discoveryCtx.updateMarket(values.market);
                discoveryCtx.updatedCompetitors(competitors)
                discoveryCtx.updateCategory(values.category);
                discoveryCtx.updateLLM(values.llm);
                discoveryCtx.updateType(discoveryCtx.type);
                discoveryCtx.updateName(`${values.brand} Brand Overview`);
                setTimeout(() => {
                  discoveryCtx.updatedLaunched(true);
                  discoveryCtx.updateLoading(true);
                  setInputValues(values);
                  setTimeout(() => {
                    discoveryCall(values)
                  }, 500)
                }, 500);
              }}
              onTypeChanged={(val) => {
                setInputValues((prevVals) => {
                  return {
                    ...prevVals,
                    type: val,
                  };
                });
              }}
            />
          </div>
          {discoveryType !== "category" && (
            <div className={classes.history}>
              <History isHistoryRefresh={isHistoryRefresh} setIsHistoryRefresh={setIsHistoryRefresh} key={childKey} filter={discoveryCtx.type} setCancelTransaction={setCancelTransaction} startPolling={startPolling}
              />
            </div>
          )}
        </div>
        {discoveryCtx.is_launched && (
          <div className={classes.result} id="print-pdf" ref={pdfRef}>
            {discoveryCtx.state.transactionStatus === "RETRY" && (
              <div className={classes.failMessage} >
                <span style={{ fontSize: "18px", marginRight: "8px" }}>⚠️</span>
                Some requests failed. Our team will retry the failed requests and fix this report.
              </div>
            )}

            {(discoveryCtx.state.transactionStatus !== "COMPLETED" &&
              discoveryCtx.state.transactionStatus !== "RETRY" &&
              discoveryCtx.is_launched &&
              (discoveryCtx.llm === "gpt-5" ||
                discoveryCtx.llm === "gpt-5-search-enabled" ||
                discoveryCtx.llm === 'GeminiV25Search' ||
                discoveryCtx.llm === "chatgpt_bd")) && (
                <div className={classes.alertMessage}>
                  <span style={{ fontSize: "18px", marginRight: "8px" }}>🕒</span>
                  This LLM’s response may take longer than usual, around 15–20 minutes.
                </div>
              )}
            {discoveryCtx?.state?.status?.totalCount &&
              <div className={classes.hideOnPrint}>
                <ProgressIndicator
                  total={discoveryCtx.state.status.totalCount}
                  completed={discoveryCtx.state.status.completedTaskCount}
                />
              </div>
            }
            <PDFHeader />
            {discoveryType !== "category" && (
              <BrandScore
                category={inputValues.category}
                market={inputValues.market}
                entity={inputValues.brand}
                remountChild={remountChild}
              />
            )}
            <div className="page-break print-only" />
            <div className={classes.section}>
              {competitors.length > 0 && (
                <Competitors
                  category={inputValues.category}
                  entity={inputValues.brand}
                />
              )}
            </div>
            {discoveryType === "brand" && discoveryCtx?.core_attributes && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors.length > 0 && (
                    <CoreAttributes
                      category={inputValues.category}
                      entity={entity}
                    />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit
                    text={coreAttributesSummary}
                    loading={coreAttributesSummary === ""}
                    firebaseNode="core_attributes"
                  />
                </div>
              </div>
            )}

            {discoveryType === "category" && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && (
                    <CategoryDimensions category={inputValues.category} />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={discoveryType != "category" || !discoveryCtx.loading}
                    text={categoryDimensionsSummary}
                    loading={categoryDimensionsSummary === ""}
                    firebaseNode="category_dimensions"
                  />
                </div>
              </div>
            )}

            {(discoveryType === "product" || discoveryType === "category") && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && <Taxonomy />}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={discoveryType != "category" || !discoveryCtx.loading}
                    text={productDimensionSummary}
                    loading={productDimensionSummary === ""}
                    firebaseNode="taxonomies"
                  />
                </div>
              </div>
            )}

            {discoveryType === "category" && (
              <div className={classes.product_recommendation_container}>
                {competitors && competitors.length > 0 && <ProductSpecs />}
              </div>
            )}

            <div className="page-break print-only" />
            {discoveryType != "category" && (
              <div className={classes.section_summary}>
                <div className={classes.flex_one}>
                  {competitors && competitors.length > 0 && (
                    <CategoryDimensions
                      category={inputValues.category}
                      entity={entity}
                    />
                  )}
                </div>
                <div className={classes.flex_one}>
                  <AIGenerated
                    edit={!discoveryCtx.loading}
                    text={categoryDimensionsSummary}
                    loading={categoryDimensionsSummary === ""}
                    firebaseNode="category_dimensions"
                  />
                </div>
              </div>
            )}
            <div className="page-break print-only" />
            {discoveryType != "category" && (
              <div className={classes.section}>
                <Sources />
              </div>
            )}
            {discoveryType !== "category" && (
              <div className={classes.section}>
                <RealtimeActions />
              </div>
            )}

            {(!discoveryCtx.state.transactionStatus) && (
              <div className={`${classes.action_btn_group} no-print`}>
                <MUIButton onClick={exportToPDF}>Export</MUIButton>
              </div>
            )}

            {(discoveryCtx.state.transactionStatus === "COMPLETED") && discoveryType != "category" && (
              <div className={`${classes.action_btn_group} no-print`}>
                <MUIButton onClick={exportToPDF}>Export</MUIButton>
                {user.customer_id == 27 && <MUIButton onClick={generateReport} loading={loading}>
                  Generate Report
                </MUIButton>}

              </div>
            )}
            <PDFFooter />
          </div>
        )}
      </section>
    </>
  );
}
